import { RouterLink } from '@angular/router';
import { Component, OnInit, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { ApiService } from '../core/services/api.service';
import { AuthService } from '../core/services/auth.service';

interface EmployeeRow {
  name: string;
  department: string;
  designation: string;
  status: string;
  avatar: string;
}

interface LeaveRequest {
  id: string;
  employee: string;
  type: string;
  dates: string;
  status: string;
  busy?: boolean;
}

interface DashboardStats {
  employees?: number;
  projects?: number;
  openTasks?: number;
  pendingLeave?: number;
  openBugs?: number;
  totalEmployees?: number;
  presentToday?: number;
  absentToday?: number;
  onLeaveToday?: number;
}

@Component({
  selector: 'app-hr-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    MatIconModule,
    MatButtonModule,
    MatCardModule,
    MatTableModule,
    MatProgressBarModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './hr-dashboard.component.html',
  styleUrl: './hr-dashboard.component.scss'
})
export class HrDashboardComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);

  loading = false;
  error: string | null = null;

  readonly displayName = this.auth.displayName;
  readonly user = this.auth.user;
  readonly firstName = computed(() => {
    const u = this.user();
    return u?.firstName || this.displayName().split(' ')[0] || 'there';
  });

  stats = [
    { title: 'Employees', value: 0, change: 'Active workforce', icon: 'groups', type: 'primary' },
    { title: 'Projects', value: 0, change: 'Active projects', icon: 'folder_special', type: 'success' },
    { title: 'Pending Leave', value: 0, change: 'Awaiting approval', icon: 'event_busy', type: 'warning' },
    { title: 'Open Bugs', value: 0, change: 'Needs attention', icon: 'bug_report', type: 'danger' }
  ];

  employees: EmployeeRow[] = [];
  leaveRequests: LeaveRequest[] = [];
  departments: { name: string; employees: number; percentage: number }[] = [];

  displayedColumns: string[] = ['employee', 'department', 'designation', 'status'];

  ngOnInit(): void {
    this.loadDashboard();
    this.loadEmployees();
    this.loadLeave();
    this.loadDepartments();
  }

  loadDashboard(): void {
    this.loading = true;
    this.error = null;

    this.api.get<DashboardStats>('v1/dashboard').subscribe({
      next: (data) => {
        this.stats[0].value = data.employees ?? data.totalEmployees ?? 0;
        this.stats[1].value = data.projects ?? 0;
        this.stats[2].value = data.pendingLeave ?? data.onLeaveToday ?? 0;
        this.stats[3].value = data.openBugs ?? data.absentToday ?? 0;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.error = err?.message || 'Failed to load dashboard stats.';
      }
    });
  }

  loadEmployees(): void {
    this.api.getPaged<Record<string, any>>('v1/Employees', { page: 1, pageSize: 8 }).subscribe({
      next: (paged) => {
        this.employees = (paged.items ?? []).map((employee) => {
          const user = employee['user'] ?? {};
          const fullName =
            user.displayName ||
            `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
            employee['name'] ||
            'Unknown';

          return {
            name: fullName,
            department: employee['departmentName'] || employee['department'] || 'N/A',
            designation: employee['jobTitle'] || employee['position'] || 'N/A',
            status: String(employee['status'] ?? 'Active'),
            avatar: this.getInitials(fullName)
          };
        });
      },
      error: () => {
        /* keep empty table */
      }
    });
  }

  loadLeave(): void {
    this.api.getPaged<Record<string, any>>('v1/leave-requests', { page: 1, pageSize: 6 }).subscribe({
      next: (paged) => {
        this.leaveRequests = (paged.items ?? []).map((leave) => ({
          id: String(leave['id'] ?? ''),
          employee:
            leave['employeeName'] ||
            leave['employee']?.displayName ||
            leave['employee']?.name ||
            'Employee',
          type: leave['leaveType'] || leave['type'] || 'Leave',
          dates: this.formatDates(leave['startDate'], leave['endDate']),
          status: String(leave['status'] ?? 'Pending')
        }));
      },
      error: () => {
        this.leaveRequests = [];
      }
    });
  }

  loadDepartments(): void {
    this.api.getPaged<Record<string, any>>('v1/Departments', { page: 1, pageSize: 10 }).subscribe({
      next: (paged) => {
        const items = paged.items ?? [];
        const total = items.reduce(
          (sum, d) => sum + Number(d['employeeCount'] ?? d['employees'] ?? 0),
          0
        ) || 1;

        this.departments = items.map((d) => {
          const count = Number(d['employeeCount'] ?? d['employees'] ?? 0);
          return {
            name: String(d['name'] ?? 'Department'),
            employees: count,
            percentage: Math.round((count / total) * 100)
          };
        });
      },
      error: () => {
        this.departments = [];
      }
    });
  }

  isLeaveActionable(status: string): boolean {
    const s = (status || '').toLowerCase();
    return [
      'pending',
      'submitted',
      'teamleadreview',
      'projectmanagerreview',
      'hrreview',
      'ceoreview'
    ].includes(s.replace(/\s+/g, ''));
  }

  approveLeave(leave: LeaveRequest): void {
    if (!leave.id || leave.busy) return;
    leave.busy = true;
    this.api.post(`v1/leave-requests/${leave.id}/approve`, { remarks: null }).subscribe({
      next: () => {
        leave.status = 'Approved';
        leave.busy = false;
        this.loadDashboard();
      },
      error: (err) => {
        leave.busy = false;
        this.error = err?.message || 'Failed to approve leave request.';
      }
    });
  }

  rejectLeave(leave: LeaveRequest): void {
    if (!leave.id || leave.busy) return;
    const reason = prompt('Rejection reason (optional):') ?? '';
    leave.busy = true;
    this.api
      .post(`v1/leave-requests/${leave.id}/reject`, { reason: reason || null, remarks: null })
      .subscribe({
        next: () => {
          leave.status = 'Rejected';
          leave.busy = false;
          this.loadDashboard();
        },
        error: (err) => {
          leave.busy = false;
          this.error = err?.message || 'Failed to reject leave request.';
        }
      });
  }

  getInitials(name: string): string {
    if (!name) return 'NA';
    return name
      .split(' ')
      .filter(Boolean)
      .map((part) => part.charAt(0))
      .join('')
      .substring(0, 2)
      .toUpperCase();
  }

  private formatDates(start?: unknown, end?: unknown): string {
    if (!start && !end) return '—';
    const s = start ? String(start).slice(0, 10) : '';
    const e = end ? String(end).slice(0, 10) : '';
    return s && e && s !== e ? `${s} - ${e}` : s || e;
  }
}
